import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Grid,
  Button,
  Typography,
  Box,
  Stack,
  Divider,
  IconButton,
  CircularProgress,
  imageListItemClasses,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { connect } from "react-redux";
import {
  Facebook,
  Instagram,
  LocationOn,
  Person2,
  PhoneEnabled,
  Twitter,
  YouTube,
} from "@mui/icons-material";
import PaymentIcon from "@mui/icons-material/Payment";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CreateOrEditSellerProfile from "./CreateOrEditSellerProfile";
import * as Actions from "../../../redux/actions/";
import ListCard from "../../../components/listing/ListCard";

const SellerProfile = ({
  jwtToken,
  seller,
  listings,
  loadListings,
  classes,
  store,
  getStore,
}) => {
  const [loading, setLoading] = useState(true);
  const [responseData, setResponseData] = useState(null);
  useEffect(() => {
    setLoading(true);
    if (seller.store) {
      getStore({ storeId: seller.store.id });
    }
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    if (store) {
      setResponseData({
        brandName: store.brand_name,
        brandLogo: store.brand_logo && store.brand_logo,
        ownerName: store.owner_name,
        location: store.location,
        phoneNumber: store.phone_number,
        paymentMethods: store.payment_methods,
        deliveryOptions: store.delivery_option,
        profileColor: store.profile_color,
        socialMedia: {
          facebook: store.facebook_url,
          instagram: store.instagram_url,
          twitter: store.twitter_url,
          youtube: store.youtube_url,
        },
        about: store.about,
        policy: store.policy,
      });
    }
  }, [store]);

  const getSlicedArray = (array, count) => {
    return array.slice(0, count);
  };

  const slicedListings = getSlicedArray(listings, 6);

  useEffect(() => {
    loadListings({ sellerId: seller.id });
  }, []);

  if (!(seller.store || store)) {
    return <CreateOrEditSellerProfile />;
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", backgroundColor: "#111" }}>
        <CircularProgress sx={{ color: "#FFC001" }} />
      </Box>
    );
  }

  const storeId = seller.store ? seller.store.id : store?.id;
  const socials = responseData ? [
    { url: responseData.socialMedia?.twitter, icon: <Twitter /> },
    { url: responseData.socialMedia?.youtube, icon: <YouTube /> },
    { url: responseData.socialMedia?.facebook, icon: <Facebook /> },
    { url: responseData.socialMedia?.instagram, icon: <Instagram /> },
  ].filter(s => s.url) : [];

  const infoRows = responseData ? [
    { icon: <Person2 sx={{ color: "#FFC001", fontSize: 15 }} />, label: responseData.ownerName },
    { icon: <LocationOn sx={{ color: "#FFC001", fontSize: 15 }} />, label: responseData.location },
    { icon: <PhoneEnabled sx={{ color: "#FFC001", fontSize: 15 }} />, label: responseData.phoneNumber },
    { icon: <PaymentIcon sx={{ color: "#FFC001", fontSize: 15 }} />, label: responseData.paymentMethods?.join(", ") },
    { icon: <DirectionsCarIcon sx={{ color: "#FFC001", fontSize: 15 }} />, label: responseData.deliveryOptions?.includes("local_pickup") ? "Local Pickup" : null },
    { icon: <LocalShippingIcon sx={{ color: "#FFC001", fontSize: 15 }} />, label: responseData.deliveryOptions?.includes("regional_pickup") ? "Regional Shipping" : null },
  ].filter(r => r.label) : [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#111", minHeight: "100vh" }}>
      {/* Page header */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: "22px", md: "28px" }, color: "#fff", lineHeight: 1.2 }}>
            Seller <Box component="span" sx={{ color: "#FFC001" }}>Profile</Box>
          </Typography>
          <Typography sx={{ color: "#555", fontSize: "13px", mt: 0.5 }}>Your store information visible to buyers</Typography>
        </Box>
        <Button
          component={Link}
          to={`/seller/profile/edit/${storeId}`}
          startIcon={<EditIcon sx={{ fontSize: "16px !important" }} />}
          sx={{ px: 3, py: 1, borderRadius: "10px", backgroundColor: "#FFC001", color: "#000", fontWeight: 800, fontSize: "13px", textTransform: "none", "&:hover": { backgroundColor: "#e6ac00" } }}
        >
          Edit Profile
        </Button>
      </Box>

      {responseData && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "260px 1fr" }, gap: 3, alignItems: "start" }}>

          {/* Left: logo + socials */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {responseData.brandLogo?.url && (
              <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 2.5, textAlign: "center" }}>
                <img
                  alt={responseData.brandName}
                  src={responseData.brandLogo.url}
                  style={{ maxWidth: "100%", maxHeight: 120, objectFit: "contain", borderRadius: 8 }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </Box>
            )}
            {socials.length > 0 && (
              <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 2.5 }}>
                <Typography sx={{ color: "#555", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", mb: 1.5 }}>Social</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {socials.map(({ url, icon }, i) => (
                    <Link key={i} to={url} target="_blank" rel="noopener noreferrer">
                      <IconButton sx={{ width: 36, height: 36, border: "1px solid #2a2a2a", color: "#666", borderRadius: "9px", "&:hover": { borderColor: "#FFC001", color: "#FFC001", backgroundColor: "rgba(255,192,1,0.06)" } }}>
                        {React.cloneElement(icon, { sx: { fontSize: 17 } })}
                      </IconButton>
                    </Link>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Right: info + about + policy + listings */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            {/* Store name + info */}
            <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "20px", color: "#fff", mb: 2 }}>{responseData.brandName}</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                {infoRows.map(({ icon, label }, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: "7px", backgroundColor: "rgba(255,192,1,0.08)", border: "1px solid rgba(255,192,1,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {icon}
                    </Box>
                    <Typography sx={{ color: "#bbb", fontSize: "14px" }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* About */}
            {responseData.about && (
              <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3 }}>
                <Typography sx={{ color: "#555", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", mb: 1.5 }}>About</Typography>
                <Typography sx={{ color: "#888", fontSize: "14px", lineHeight: 1.9 }}>{responseData.about}</Typography>
              </Box>
            )}

            {/* Policy */}
            {responseData.policy && (
              <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3 }}>
                <Typography sx={{ color: "#555", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", mb: 1.5 }}>Store Policy</Typography>
                <Typography sx={{ color: "#888", fontSize: "14px", lineHeight: 1.9 }}>{responseData.policy}</Typography>
              </Box>
            )}

            {/* Listings preview */}
            {slicedListings.length > 0 && (
              <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3 }}>
                <Typography sx={{ color: "#555", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", mb: 2 }}>Listings</Typography>
                <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" } }}>
                  <ListCard listData={slicedListings} classes={classes} />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const mapStateToProps = ({ Auth, Seller, Store, Listings, Categories }) => ({
  jwtToken: Auth.jwtToken,
  seller: Seller.seller,
  store: Store.store,
  listings: Listings.listings,
  loading: Listings.loading,
  totalListing: Listings.totalListing,
  classes: Categories.classes,
});

const mapDispatchToProps = (dispatch) => ({
  loadListings: (sellerId) => dispatch(Actions.thunkLoadListing(sellerId)),
  getStore: (storeId) => dispatch(Actions.thunkGetStore(storeId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SellerProfile);
